/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed, get } from '@ember/object';
import { service } from '@ember/service';
import { alias } from '@ember/object/computed';
import { combineGoals, groupGoalsBySubcategory } from 'linced-app/utils/topics';
import topicSteps from '@linc-technologies/linced-common/utils/topic-steps';

export default class TopicController extends Controller {
  @service
  features;

  @service
  strings;

  @tracked
  tab = 0;

  @action
  setTab(value) {
    this.tab = value;
  }

  @tracked
  steps = null;

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @computed('model.goal.linkedGoals.content.[]')
  get sortedCoreGoals() {
    const modelGoal = this.get('model.goal');
    if (!modelGoal) {
      return {};
    }
    let linkedGoals = modelGoal.get('linkedGoals') || [];
    return groupGoalsBySubcategory(linkedGoals);
  }

  @computed('model.goal.priorLinkedGoals.content.[]')
  get sortedPriorGoals() {
    const modelGoal = this.get('model.goal');

    if (!modelGoal) {
      return {};
    }
    let linkedGoals = modelGoal.get('priorLinkedGoals') || [];
    return groupGoalsBySubcategory(linkedGoals);
  }

  @computed('model.goal.futureLinkedGoals.content.[]')
  get sortedFutureGoals() {
    const modelGoal = this.get('model.goal');
    if (!modelGoal) {
      return {};
    }
    let linkedGoals = modelGoal.get('futureLinkedGoals') || [];
    return groupGoalsBySubcategory(linkedGoals);
  }

  @computed('sortedCoreGoals', 'sortedPriorGoals', 'sortedFutureGoals')
  get combinedGoalsMatrix() {
    return combineGoals(
      this.sortedCoreGoals,
      this.sortedPriorGoals,
      this.sortedFutureGoals,
    );
  }

  init() {
    super.init(...arguments);
    this.steps = topicSteps;
    this.subjectTypeLinkedGoals;
  }

  @computed(
    'model.goal.linkedGoals.content.[]', // Track individual record changes
    'model.allCategories.@each.{id,label}', // Track individual record changes
    'model.allSubjects.@each.{id,label,subjectTypeName}',
  )
  get subjectTypeLinkedGoals() {
    const model = this.model;
    if (!model || !model.goal) {
      return {};
    }

    let linkedGoals = this.get('model.goal.linkedGoals') || [];
    let allSubjects = this.get('model.allSubjects') || [];
    let allCategories = this.get('model.allCategories') || [];

    // Check if the store collections are still loading
    const subjectsLoading = this.get('model.allSubjects.isLoading');
    const categoriesLoading = this.get('model.allCategories.isLoading');

    if (subjectsLoading || categoriesLoading) {
      return {};
    }

    // If any of the lookup arrays are empty, the data might not be loaded yet
    if (
      linkedGoals.length > 0 &&
      (allSubjects.length === 0 || allCategories.length === 0)
    ) {
      return {};
    }

    let groupedGoals = {};

    // Group linked goals by their subject, using allSubjects to get the subject label and type
    linkedGoals.forEach((goal) => {
      let subjectId = get(goal, 'subject_id');
      let categoryId = get(goal, 'category_id');

      if (!subjectId || !categoryId) {
        return; // Skip goals without required IDs
      }

      // Find the corresponding subject from allSubjects
      let subject = allSubjects.find((subject) => subject.id === subjectId);
      let subjectLabel = subject ? get(subject, 'label') : 'Unknown';
      let subjectType = subject ? get(subject, 'subjectTypeName') : 'Not set';

      // Find the corresponding category from allCategories
      let category = allCategories.find(
        (category) => category.id === categoryId,
      );
      let categoryLabel = category ? get(category, 'label') : 'Unknown';

      // Create consistent label key
      let labelKey = `${subjectLabel} - ${categoryLabel}`;

      // Initialize the object for this subjectType if it doesn't exist
      if (!groupedGoals[subjectType]) {
        groupedGoals[subjectType] = {};
      }

      // Initialize the array for this labelKey if it doesn't exist
      if (!groupedGoals[subjectType][labelKey]) {
        groupedGoals[subjectType][labelKey] = [];
      }

      // Push the goal into the corresponding array using the same labelKey
      groupedGoals[subjectType][labelKey].push(goal);
    });

    return groupedGoals;
  }
}
